import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User as UserIcon, MessageSquare } from "lucide-react";

export default function BoardPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Board inquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/board")
      .then(({ data }) => setMembers(data))
      .catch(() => toast.error("Could not load board directory"))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — the board will reply within 2 business days.");
      setForm({ name: "", email: "", phone: "", subject: "Board inquiry", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="board-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Community</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421]">Meet the Board</h1>
        <p className="text-[#5C6A64] mt-3 max-w-2xl">
          Our HOA is entirely resident-led. Board members are volunteers from Saddleridge, Savoy Chase, and The Trails who steward the community on behalf of every homeowner.
        </p>
      </div>

      {loading ? (
        <p className="text-[#5C6A64]">Loading directory…</p>
      ) : members.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]"><CardContent className="p-12 text-center text-[#5C6A64]">Board members have not yet published their profiles. Check back soon.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="board-grid">
          {members.map((m) => (
            <Card key={m.id} data-testid={`board-member-${m.id}`} className="rounded-2xl border-[#D5CFC4] hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-[#EADCC8] flex items-center justify-center overflow-hidden">
                  {m.avatar_url
                    ? <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                    : <UserIcon className="w-10 h-10 text-[#2C4C3B]" />}
                </div>
                <p className="font-serif text-xl text-[#1A2421]">{m.name}</p>
                <Badge className="mt-2 bg-[#2C4C3B] text-white hover:bg-[#233D2F]">{m.board_title}</Badge>
                {m.board_bio && <p className="text-sm text-[#5C6A64] mt-3 italic">"{m.board_bio}"</p>}
                <a
                  href="#board-contact"
                  data-testid={`board-contact-link-${m.id}`}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#C86650] hover:underline"
                >
                  <MessageSquare className="w-4 h-4" /> Contact the board
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Contact form at bottom */}
      <section className="mt-20 border-t border-[#D5CFC4] pt-16" id="board-contact">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Get in touch</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1A2421] mb-4">Message the Board</h2>
            <p className="text-[#5C6A64] leading-relaxed">
              Have a question, concern, or amenity request? Send us a note. We usually respond within two business days.
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm text-[#1A2421]">
                <MessageSquare className="w-4 h-4 text-[#2C4C3B]" />
                <span>saddleridgesavoytrailshoa@gmail.com</span>
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-3 space-y-4" data-testid="board-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="board-contact-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" />
              <Input data-testid="board-contact-email" required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" />
            </div>
            <Input data-testid="board-contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" />
            <Input data-testid="board-contact-subject" required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-xl" />
            <Textarea data-testid="board-contact-message" required rows={5} placeholder="Your message…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" />
            <Button data-testid="board-contact-submit" type="submit" disabled={submitting} className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white">
              {submitting ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
